import { getFormTabErrorCounts } from '../../../../../../lib/formTabErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFormState, useWatch, type FieldErrors } from 'react-hook-form';
import { FileStack, Layers, ListChecks } from 'lucide-react';
import {
  DashboardPageFormActions,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { EditorSectionTabs } from '../../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../../components/forms/useEditorSection';
import { dashboardT } from '../../../../../../lib/messages';
import { createCreativeFormSchema, type CreativeFormValue } from '../../../creativeFormSchema';
import { CreativeType } from '../../../model';
import { CreativeGeneralFields } from '../CreativeGeneralFields';
import { CreativeTrackersFields } from '../CreativeTrackersFields';
import { CreativeVariantsFields } from '../CreativeVariantsFields';

enum CreativeFormTab {
  General = 'general',
  Trackers = 'trackers',
  Variants = 'variants',
}

export function CreativeEditorForm({
  initialValue,
  editMode,
  saving,
  saveDisabled = false,
  onCancel,
  onSubmit,
}: {
  initialValue: CreativeFormValue;
  editMode: boolean;
  saving: boolean;
  saveDisabled?: boolean;
  onCancel: () => void;
  onSubmit: (value: CreativeFormValue) => Promise<void>;
}) {
  'use no memo';
  const form = useForm<CreativeFormValue>({
    defaultValues: initialValue,
    resolver: zodResolver(createCreativeFormSchema({ editMode })),
  });
  const { errors } = useFormState({ control: form.control });
  const type = useWatch({ control: form.control, name: 'type' });
  const [editorSection, setActiveTab] = useEditorSectionControls(
    CreativeFormTab.General,
    Object.values(CreativeFormTab).filter(
      (tab) => tab !== CreativeFormTab.Variants || (editMode && type === CreativeType.Video),
    ),
  );
  async function submit(value: CreativeFormValue) {
    if (saveDisabled) return;
    form.clearErrors('root.validation');
    form.clearErrors('root.server');
    try {
      await onSubmit(value);
    } catch (error) {
      form.setError('root.server', {
        message: error instanceof Error ? error.message : dashboardT('creatives.saveError'),
      });
    }
  }
  const tabErrors = getFormTabErrorCounts(
    errors,
    {
      [CreativeFormTab.Trackers]: [
        'impressionTrackers',
        'trackerStart',
        'trackerFirstQuartile',
        'trackerMidpoint',
        'trackerThirdQuartile',
        'trackerComplete',
      ],
      [CreativeFormTab.Variants]: ['variants'],
    },
    CreativeFormTab.General,
  );

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={(event) => {
          void form.handleSubmit(submit, (validationErrors) => {
            form.setError('root.validation', {
              message: dashboardT('creatives.validation.summary'),
            });
            setActiveTab(
              hasTrackerErrors(validationErrors)
                ? CreativeFormTab.Trackers
                : CreativeFormTab.General,
            );
          })(event);
        }}
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList>
            <DashboardPageFormTab
              value={CreativeFormTab.General}
              errorCount={tabErrors[CreativeFormTab.General]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<ListChecks className="h-4 w-4" />}
            >
              {dashboardT('creatives.general')}
            </DashboardPageFormTab>
            <DashboardPageFormTab
              value={CreativeFormTab.Trackers}
              errorCount={tabErrors[CreativeFormTab.Trackers]}
              errorLabel={(count) => dashboardT('common.validationErrors', { count })}
              icon={<FileStack className="h-4 w-4" />}
            >
              {dashboardT('creatives.trackers')}
            </DashboardPageFormTab>
            {editMode && type === CreativeType.Video ? (
              <DashboardPageFormTab
                value={CreativeFormTab.Variants}
                errorCount={tabErrors[CreativeFormTab.Variants]}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
                icon={<Layers className="h-4 w-4" />}
              >
                {dashboardT('creatives.variants')}
              </DashboardPageFormTab>
            ) : null}
          </DashboardPageFormTabList>
          <DashboardPageFormTabPanel value={CreativeFormTab.General}>
            <CreativeGeneralFields editMode={editMode} />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={CreativeFormTab.Trackers}>
            <CreativeTrackersFields />
          </DashboardPageFormTabPanel>
          <DashboardPageFormTabPanel value={CreativeFormTab.Variants}>
            <CreativeVariantsFields />
          </DashboardPageFormTabPanel>
        </EditorSectionTabs>
        {errors.root?.validation?.message || errors.root?.server?.message ? (
          <p role="alert" className="text-destructive mx-auto max-w-3xl px-5 pb-4 text-sm">
            {errors.root.validation?.message ?? errors.root.server?.message}
          </p>
        ) : null}
        <DashboardPageFormActions>
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-5"
            disabled={saving}
            onClick={onCancel}
          >
            {dashboardT('creatives.cancel')}
          </Button>
          <Button type="submit" className="rounded-full px-5" disabled={saving || saveDisabled}>
            {saving ? dashboardT('creatives.saving') : dashboardT('creatives.save')}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}

function hasTrackerErrors(errors: FieldErrors<CreativeFormValue>) {
  return Boolean(
    errors.impressionTrackers ||
    errors.trackerStart ||
    errors.trackerFirstQuartile ||
    errors.trackerMidpoint ||
    errors.trackerThirdQuartile ||
    errors.trackerComplete,
  );
}
